import { useMemo } from 'react';
import { useGetUserProfileV2Query } from '../services/merchant';

// Types for the hook return value
export type UseUserProfileReturn = {
  // States
  isLoading: boolean;
  error: Error | null;
  isReady: boolean;

  // Currently used fields
  country: string;
  serviceId: string;
  merchantId: string;
  isObserver: boolean;

  // Raw API data (for accessing other fields when needed)
  apiData?: any;
};

export const useUserProfile = (): UseUserProfileReturn => {
  // Fetch data from API
  const { data: apiData, error: apiError, isLoading: apiIsLoading } = useGetUserProfileV2Query();

  // Extract currently used fields
  const country = useMemo(() => {
    return apiData?.country || 'SG';
  }, [apiData]);

  const serviceId = useMemo(() => {
    return apiData?.user_profile_id || '';
  }, [apiData]);

  const merchantId = useMemo(() => {
    if (!apiData?.user_profile?.links) {
      return '';
    }
    const { links } = apiData.user_profile;
    const gfLink = links.find((link) => link?.link_entity_business_line === 'GF');
    return gfLink?.link_entity_id || '';
  }, [apiData]);

  const isObserver = useMemo(() => {
    return apiData?.user_profile?.is_observer || false;
  }, [apiData]);

  const isReady = useMemo(() => {
    return !apiIsLoading && !apiError && !!apiData;
  }, [apiIsLoading, apiError, apiData]);

  return {
    isLoading: apiIsLoading,
    isReady,
    error: apiError as Error | null,
    serviceId,
    country,
    merchantId,
    isObserver,
    apiData,
  };
};

export default useUserProfile;
